(() => {
  if (customElements.get('newsletter-popup')) return;
  const SEEN = 'songmont:newsletter-popup:seen';
  const SUBSCRIBED = 'songmont:newsletter-popup:submitted';
  const PENDING = 'songmont:newsletter-popup:pending';
  const read = (storage, key) => { try { return window[storage].getItem(key); } catch { return null; } };
  const write = (storage, key, value) => { try { window[storage].setItem(key, value); } catch { /* Storage may be unavailable. */ } };
  const remove = (storage, key) => { try { window[storage].removeItem(key); } catch { /* Keep the form usable. */ } };

  customElements.define('newsletter-popup', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.editor = this.dataset.editor === 'true';
      this.form = this.querySelector('form');
      this.querySelector('[data-popup-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => this.restorePage(), options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const targets = [...this.dialog.querySelectorAll('button, input, a[href], [tabindex="0"]')]
          .filter(el => !el.disabled && el.getClientRects().length);
        const first = targets[0];
        const last = targets[targets.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first?.focus();
        }
      }, options);
      this.form.addEventListener('submit', event => {
        if (this.editor) {
          event.preventDefault();
          const template = this.querySelector('[data-popup-success-preview]');
          if (template) {
            this.showSuccess(template);
          }
          return;
        }
        // Respect validation/captcha handlers that have already stopped this submission.
        if (event.defaultPrevented) return;
        event.preventDefault();
        this.submitInPlace();
      }, options);
      document.addEventListener('submit', event => {
        if (event.target !== this.form) remove('sessionStorage', PENDING);
      }, options);
      if (this.editor) {
        this.querySelector('[data-popup-preview]')?.addEventListener('click', () => this.open(false), options);
        this.closest('.shopify-section')?.addEventListener('shopify:section:select', () => this.open(false), options);
        this.closest('.shopify-section')?.addEventListener('shopify:section:deselect', () => this.dialog.close(), options);
        return;
      }
      const result = this.querySelector('[data-popup-result]').dataset.popupResult;
      const pending = Number(read('sessionStorage', PENDING));
      const recentSubmission = pending > 0 && Date.now() - pending >= 0 && Date.now() - pending < 30 * 60 * 1000;
      const returnedToPopup = window.location.hash === '#NewsletterPopupForm';
      // A server-rendered result is required; the hash or local flag alone never implies success.
      const ownResult = result && (recentSubmission || returnedToPopup);
      if (ownResult) {
        remove('sessionStorage', PENDING);
        if (result === 'success') write('localStorage', SUBSCRIBED, '1');
        this.open(true);
        this.querySelector(result === 'success' ? '[role="status"]' : 'input[type="email"]')?.focus();
        return;
      }
      if (this.dataset.enabled !== 'true' || this.dataset.subscribed === 'true' || read('localStorage', SUBSCRIBED) || read('sessionStorage', SEEN)) return;
      // Do not interpret a success/error from the footer's customer form as ours.
      if (result) return;
      this.onScroll = () => {
        if (window.scrollY < Number(this.dataset.scroll || 400) || document.hidden || document.querySelector('dialog[open], [aria-modal="true"][aria-hidden="false"]')) return;
        if (this.open(true)) window.removeEventListener('scroll', this.onScroll);
      };
      window.addEventListener('scroll', this.onScroll, { ...options, passive: true });
      this.onScroll();
    }
    async submitInPlace() {
      if (this.submitting || !this.form.reportValidity()) return;
      this.submitting = true;
      const button = this.form.querySelector('button[type="submit"], button:not([type])');
      const label = button.textContent;
      const result = this.querySelector('[data-popup-result]');
      result.querySelector('[data-popup-request-error]')?.remove();
      const body = new URLSearchParams(new FormData(this.form));
      button.disabled = true;
      button.classList.add('is-loading');
      button.textContent = this.dataset.loadingLabel || 'Submitting…';
      this.form.setAttribute('aria-busy', 'true');
      write('sessionStorage', PENDING, String(Date.now()));
      this.requestController = new AbortController();
      const timeout = setTimeout(() => this.requestController.abort(), 20000);
      try {
        const response = await fetch(this.form.action, {
          method: 'POST', body, credentials: 'same-origin',
          headers: { Accept: 'text/html' }, signal: this.requestController.signal
        });
        const url = new URL(response.url, window.location.href);
        if (url.origin !== window.location.origin) throw new Error('Unexpected response origin');
        if (/(^|\/)challenge\/?$/.test(url.pathname)) {
          // Shopify requires an interactive challenge: continue its redirect without reposting.
          window.location.assign(url.href);
          return;
        }
        if (!response.ok) throw new Error('Subscription request failed');
        const template = this.querySelector('[data-popup-success-preview]');
        if (response.redirected && url.searchParams.get('customer_posted') === 'true' && template) {
          // Shopify has already confirmed success in its redirect; skip downloading/parsing the full page.
          response.body?.cancel().catch(() => {});
          remove('sessionStorage', PENDING);
          write('localStorage', SUBSCRIBED, '1');
          await this.showSuccess(template);
          return;
        }
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const returned = html.querySelector('#NewsletterPopupForm [data-popup-result]');
        const serverError = returned?.querySelector('[role="alert"], .newsletter-popup__error');
        if (returned?.dataset.popupResult === 'error' || serverError) {
          this.showRequestError(serverError?.textContent?.trim());
          remove('sessionStorage', PENDING);
          return;
        }
        const success = returned?.dataset.popupResult === 'success' || url.searchParams.get('customer_posted') === 'true';
        if (!success || !template) throw new Error('No confirmed subscription result');
        remove('sessionStorage', PENDING);
        write('localStorage', SUBSCRIBED, '1');
        await this.showSuccess(template);
      } catch (error) {
        if (this.isConnected) this.showRequestError();
      } finally {
        clearTimeout(timeout);
        this.submitting = false;
        button.disabled = false;
        button.classList.remove('is-loading');
        button.textContent = label;
        this.form.removeAttribute('aria-busy');
      }
    }
    async showSuccess(template) {
      if (this.transitioning) return;
      this.transitioning = true;
      const result = this.querySelector('[data-popup-result]');
      const content = this.querySelector('.newsletter-popup__content');
      const motion = this.dialog.open && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const before = content.getBoundingClientRect().height;
      const style = getComputedStyle(content);
      const oldPadding = { paddingTop: style.paddingTop, paddingBottom: style.paddingBottom };
      try {
        if (motion) await result.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 100, fill: 'forwards' }).finished;
        result.replaceChildren(template.content.cloneNode(true));
        result.dataset.popupResult = 'success';
        const after = content.getBoundingClientRect().height;
        const newStyle = getComputedStyle(content);
        result.getAnimations().forEach(animation => animation.cancel());
        if (motion && this.dialog.open) {
          const resize = content.animate([
            { height: `${before}px`, ...oldPadding },
            { height: `${after}px`, paddingTop: newStyle.paddingTop, paddingBottom: newStyle.paddingBottom }
          ], { duration: 260, easing: 'cubic-bezier(.22, 1, .36, 1)' });
          const reveal = result.animate([{ opacity: 0, transform: 'translateY(5px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 220, easing: 'ease-out' });
          await Promise.all([resize.finished, reveal.finished]);
        }
        if (this.dialog.open) result.querySelector('[role="status"]')?.focus({ preventScroll: true });
      } finally {
        this.transitioning = false;
      }
    }
    showRequestError(message) {
      const result = this.querySelector('[data-popup-result]');
      result.querySelector('[data-popup-request-error]')?.remove();
      const error = document.createElement('p');
      error.className = 'newsletter-popup__error';
      error.dataset.popupRequestError = '';
      error.setAttribute('role', 'alert');
      error.tabIndex = -1;
      error.textContent = message || this.dataset.errorLabel || 'We could not confirm your signup. Please try again.';
      result.append(error);
      if (this.dialog.open) error.focus();
    }
    open(remember) {
      if (this.dialog.open) return false;
      this.previousFocus = document.activeElement;
      this.dialog.showModal();
      // Focus the panel initially, not the close icon; keyboard controls retain their focus rings.
      this.dialog.focus({ preventScroll: true });
      document.documentElement.classList.add('newsletter-popup-open');
      if (remember) write('sessionStorage', SEEN, '1');
      return true;
    }
    restorePage() {
      document.documentElement.classList.remove('newsletter-popup-open');
      if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.requestController?.abort();
      if (this.dialog?.open) { this.dialog.close(); this.restorePage(); }
    }
  });
})();
